import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, Paper, Grid, Tabs, Tab,
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, CircularProgress, TextField, InputAdornment,
    Snackbar, Alert, Chip, Divider, Slider, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Assessment as AssessmentIcon,
    FilePresent as FileIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import flaskServer from '../config/flaskserver';
import { styled } from '@mui/material/styles';

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 700,
    padding: '10px 24px',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
}));

const AuditoriaSus = () => {
    const [tabValue, setTabValue] = useState(0);
    const [subTab, setSubTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [aihs, setAihs] = useState([]);
    const [patterns, setPatterns] = useState([]);
    const [history, setHistory] = useState([]);
    const [findings, setFindings] = useState([]);
    const [summary, setSummary] = useState({ 'Crítica': 0, 'Muito Alta': 0, 'Alta': 0, 'Média': 0, 'Baixa': 0 });
    const [topProcs, setTopProcs] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedSeverity, setSelectedSeverity] = useState('Todas');
    const [patternSearch, setPatternSearch] = useState('');
    const [reprocessing, setReprocessing] = useState(false);
    const [minConfidence, setMinConfidence] = useState(30);
    const [search, setSearch] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchAihs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/aihs?search=${search}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAihs(response.data.aihs || []);
        } catch (error) {
            console.error("Erro ao buscar AIHs", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPatterns = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/analysis/co-occurrence`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPatterns(response.data.patterns || []);
        } catch (error) {
            console.error("Erro ao buscar padrões", error);
        }
    };

    const fetchFindings = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/audit/findings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setFindings(response.data.findings || []);
        } catch (error) {
            console.error("Erro ao buscar achados", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = { start_date: startDate, end_date: endDate, severity: selectedSeverity };
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/analysis/history`, {
                params,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setHistory(response.data.history || []);
        } catch (error) {
            console.error("Erro ao buscar histórico", error);
        }
    };

    const fetchSummary = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = { start_date: startDate, end_date: endDate };
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/analysis/summary`, {
                params,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSummary(response.data.summary || { 'Crítica': 0, 'Muito Alta': 0, 'Alta': 0, 'Média': 0, 'Baixa': 0 });
        } catch (error) {
            console.error("Erro ao buscar resumo", error);
        }
    };

    const fetchTopProcedures = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = { start_date: startDate, end_date: endDate, severity: selectedSeverity };
            const response = await axios.get(`${flaskServer.flaskServerIP}/sus/analysis/top-procedures`, {
                params,
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setTopProcs(response.data.top_procs || []);
        } catch (error) {
            console.error("Erro ao buscar top procedimentos", error);
        }
    };

    useEffect(() => {
        if (tabValue === 0) {
            fetchHistory();
            fetchSummary();
            fetchTopProcedures();
        }
        if (tabValue === 1) fetchPatterns();
        if (tabValue === 2) fetchAihs();
        if (tabValue === 3) fetchFindings();
    }, [tabValue, startDate, endDate, minConfidence, selectedSeverity]);

    const handleReprocess = async () => {
        if (!window.confirm("Isso irá apagar os achados atuais e re-analisar todas as AIHs importadas. Deseja continuar?")) return;
        setReprocessing(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${flaskServer.flaskServerIP}/sus/audit/reprocess`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            fetchFindings();
            fetchHistory();
        } catch (error) {
            console.error("Erro no reprocessamento", error);
            setSnackbar({ open: true, message: "Erro ao reprocessar dados.", severity: 'error' });
        } finally {
            setReprocessing(false);
        }
    };

    const handleImportSISAIH = async (file) => {
        if (!file) return;
        setImporting(true);
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${flaskServer.flaskServerIP}/sus/aihs/upload`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setSnackbar({ open: true, message: 'Arquivo SISAIH importado com sucesso!', severity: 'success' });
            if (tabValue === 1) fetchAihs();
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao importar arquivo SISAIH', severity: 'error' });
        } finally {
            setImporting(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight={800} sx={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    Auditoria AIH
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {tabValue === 0 && (
                        <>
                            <TextField
                                label="Início"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                sx={{ bgcolor: 'white', borderRadius: 1 }}
                            />
                            <TextField
                                label="Fim"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                sx={{ bgcolor: 'white', borderRadius: 1 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Criticidade</InputLabel>
                                <Select
                                    value={selectedSeverity}
                                    onChange={(e) => setSelectedSeverity(e.target.value)}
                                    label="Criticidade"
                                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                                >
                                    <MenuItem value="Todas">Todas</MenuItem>
                                    <MenuItem value="Crítica">🚨 Crítica</MenuItem>
                                    <MenuItem value="Muito Alta">🔴 Muito Alta</MenuItem>
                                    <MenuItem value="Alta">🟠 Alta</MenuItem>
                                    <MenuItem value="Média">🟡 Média</MenuItem>
                                    <MenuItem value="Baixa">🔵 Baixa</MenuItem>
                                </Select>
                            </FormControl>
                        </>
                    )}
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{
                        '& .MuiTab-root': { fontWeight: 700, textTransform: 'none' }
                    }}>
                        <Tab label="Dashboard" icon={<AssessmentIcon />} iconPosition="start" />
                        <Tab label="Padrões de Cobrança" />
                        <Tab label="Importação" />
                        <Tab label="Achados" icon={<CheckCircleIcon />} iconPosition="start" />
                    </Tabs>
                </Box>
            </Box>

            {tabValue === 0 && (
                <Grid container spacing={2}>
                    {/* Summary Cards */}
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Paper sx={{ p: 2, borderRadius: 4, textAlign: 'center', bgcolor: '#7f1d1d', color: 'white' }}>
                            <Typography variant="overline" sx={{ opacity: 0.9, fontWeight: 700 }}>🚨 Crítica</Typography>
                            <Typography variant="h4" fontWeight={800}>{summary['Crítica'] || 0}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Paper sx={{ p: 2, borderRadius: 4, textAlign: 'center', bgcolor: '#ef4444', color: 'white' }}>
                            <Typography variant="overline" sx={{ opacity: 0.9, fontWeight: 700 }}>🔴 Muito Alta</Typography>
                            <Typography variant="h4" fontWeight={800}>{summary['Muito Alta'] || 0}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Paper sx={{ p: 2, borderRadius: 4, textAlign: 'center', bgcolor: '#fee2e2', color: '#b91c1c' }}>
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>🟠 Alta</Typography>
                            <Typography variant="h4" fontWeight={800}>{summary['Alta'] || 0}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Paper sx={{ p: 2, borderRadius: 4, textAlign: 'center', bgcolor: '#fef3c7', color: '#92400e' }}>
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>🟡 Média</Typography>
                            <Typography variant="h4" fontWeight={800}>{summary['Média'] || 0}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Paper sx={{ p: 2, borderRadius: 4, textAlign: 'center', bgcolor: '#e0f2fe', color: '#0369a1' }}>
                            <Typography variant="overline" sx={{ fontWeight: 700 }}>🔵 Baixa</Typography>
                            <Typography variant="h4" fontWeight={800}>{summary['Baixa'] || 0}</Typography>
                        </Paper>
                    </Grid>

                    {/* Chart Row 1 */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Histórico de Inconsistências</Typography>
                            <Box sx={{ height: 300, width: '100%' }}>
                                <BarChart
                                    xAxis={[{ scaleType: 'band', data: history.map(h => h.data.split('-').slice(1).join('/')) }]}
                                    series={[
                                        { data: history.map(h => h['Crítica'] || 0), color: '#7f1d1d', stack: 'total', label: 'Crítica' },
                                        { data: history.map(h => h['Muito Alta'] || 0), color: '#ef4444', stack: 'total', label: 'Muito Alta' },
                                        { data: history.map(h => h['Alta'] || 0), color: '#F87171', stack: 'total', label: 'Alta' },
                                        { data: history.map(h => h['Média'] || 0), color: '#FBBF24', stack: 'total', label: 'Média' },
                                        { data: history.map(h => h['Baixa'] || 0), color: '#60A5FA', stack: 'total', label: 'Baixa' }
                                    ]}
                                    height={300}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Distribuição de Criticidade</Typography>
                            <Box sx={{ height: 300, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <PieChart
                                    series={[{
                                        data: [
                                            { id: 0, value: summary['Crítica'], label: 'Crítica', color: '#7f1d1d' },
                                            { id: 1, value: summary['Muito Alta'], label: 'M. Alta', color: '#ef4444' },
                                            { id: 2, value: summary['Alta'], label: 'Alta', color: '#F87171' },
                                            { id: 3, value: summary['Média'], label: 'Média', color: '#FBBF24' },
                                            { id: 4, value: summary['Baixa'], label: 'Baixa', color: '#60A5FA' }
                                        ],
                                        innerRadius: 60,
                                        paddingAngle: 5,
                                        cornerRadius: 5,
                                    }]}
                                    height={300}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Chart Row 2 */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Top 10 Procedimentos com Omissões</Typography>
                            <Box sx={{ height: 350, width: '100%' }}>
                                <BarChart
                                    layout="horizontal"
                                    yAxis={[{ scaleType: 'band', data: topProcs.map(p => p.procedimento) }]}
                                    series={[{ data: topProcs.map(p => p.quantidade), color: '#6366f1', label: 'Total de Omissões' }]}
                                    height={350}
                                    margin={{ left: 100 }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tabValue === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1, justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AssessmentIcon color="primary" />
                                    <Typography variant="h6" fontWeight={700}>Padrões de Cobrança (Co-ocorrência)</Typography>
                                </Box>
                                <TextField
                                    placeholder="Filtrar padrão..."
                                    size="small"
                                    value={patternSearch}
                                    onChange={(e) => setPatternSearch(e.target.value)}
                                    sx={{ width: 200 }}
                                />
                            </Box>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.9)' }}>Proc. Principal</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.9)' }}>Proc. Secundário</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.9)' }} align="center">Frequência</TableCell>
                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.9)' }} align="center">Confiança</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {patterns.filter(p =>
                                            p.procedimento_principal.includes(patternSearch) ||
                                            p.procedimento_secundario.includes(patternSearch)
                                        ).length === 0 ? (
                                            <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>Nenhum padrão encontrado para o filtro.</TableCell></TableRow>
                                        ) : patterns.filter(p =>
                                            p.procedimento_principal.includes(patternSearch) ||
                                            p.procedimento_secundario.includes(patternSearch)
                                        ).map((p, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{p.procedimento_principal}</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: 'secondary.main' }}>{p.procedimento_secundario}</TableCell>
                                                <TableCell align="center">
                                                    <Chip label={p.frequencia} size="small" sx={{ fontWeight: 700 }} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" fontWeight={700} color="success.main">
                                                        {((p.frequencia / p.total_principal) * 100).toFixed(1)}%
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tabValue === 2 && (
                <Paper sx={{ p: 3, borderRadius: 4 }}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                placeholder="Buscar AIH, Paciente ou CNES..."
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && fetchAihs()}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                }}
                                sx={{ width: 300 }}
                            />
                            <Button variant="outlined" onClick={fetchAihs} startIcon={<SearchIcon />}>Filtrar</Button>
                        </Box>
                        <Box>
                            <Button
                                component="label"
                                variant="contained"
                                startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                disabled={importing}
                                sx={{ borderRadius: 2, px: 3 }}
                            >
                                {importing ? 'Importando...' : 'Importar SISAIR01'}
                                <input type="file" hidden onChange={(e) => handleImportSISAIH(e.target.files[0])} />
                            </Button>
                        </Box>
                    </Box>

                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>AIH</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>CNES</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Apresentação</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Procedimento</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Datas</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={7} align="center"><CircularProgress sx={{ my: 4 }} /></TableCell></TableRow>
                                ) : aihs.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Nenhum registro encontrado (SISAIH01).</TableCell></TableRow>
                                ) : aihs.map((aih) => (
                                    <TableRow key={aih.id} hover>
                                        <TableCell sx={{ fontWeight: 700 }}>{aih.nu_aih}</TableCell>
                                        <TableCell>{aih.cnes_hosp}</TableCell>
                                        <TableCell>{aih.apres_lote}</TableCell>
                                        <TableCell>{aih.nm_paciente}</TableCell>
                                        <TableCell>
                                            <Tooltip title={aih.proc_realizado}>
                                                <Typography variant="body2" sx={{ maxWidth: 200, noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {aih.proc_realizado}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block">{aih.dt_intern} a {aih.dt_saida}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={aih.ident_aih === '01' ? 'Principal' : aih.ident_aih}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )
            }

            {
                tabValue === 3 && (
                    <Paper sx={{ p: 3, borderRadius: 4 }}>
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon color="error" />
                                <Typography variant="h6" fontWeight={700}>Inconsistências e Omissões Detectadas</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexGrow: 1, maxWidth: 400, ml: 4 }}>
                                <Typography variant="body2" sx={{ minWidth: 150 }}>Confiança Mínima: <strong>{minConfidence}%</strong></Typography>
                                <Slider
                                    value={minConfidence}
                                    onChange={(e, val) => setMinConfidence(val)}
                                    min={1}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    sx={{ color: 'primary.main' }}
                                />
                            </Box>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={reprocessing ? <CircularProgress size={20} /> : <RefreshIcon />}
                                onClick={handleReprocess}
                                disabled={reprocessing}
                                sx={{ borderRadius: 2 }}
                            >
                                {reprocessing ? 'Reprocessando...' : 'Reprocessar Tudo'}
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'rgba(239, 68, 68, 0.05)' }}>
                                        <TableCell sx={{ fontWeight: 700 }}>AIH</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Criticidade</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && findings.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
                                    ) : findings.filter(f => (f.confidencia * 100) >= minConfidence).length === 0 ? (
                                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Nenhuma inconsistência detectada para os filtros aplicados.</TableCell></TableRow>
                                    ) : findings.filter(f => (f.confidencia * 100) >= minConfidence).map((f) => (
                                        <TableRow key={f.id} hover>
                                            <TableCell sx={{ fontWeight: 700 }}>{f.nu_aih}</TableCell>
                                            <TableCell>
                                                <Chip label={f.tipo_falha} size="small" color={f.tipo_falha === 'Omissão' ? 'warning' : 'error'} variant="outlined" />
                                            </TableCell>
                                            <TableCell>{f.descricao}</TableCell>
                                            <TableCell>
                                                <Chip label={f.criticidade} size="small" sx={{
                                                    bgcolor: f.criticidade === 'Crítica' ? '#7f1d1d' :
                                                        f.criticidade === 'Muito Alta' ? '#ef4444' :
                                                            f.criticidade === 'Alta' ? '#FEE2E2' :
                                                                f.criticidade === 'Média' ? '#FEF3C7' : '#E0F2FE',
                                                    color: f.criticidade === 'Crítica' || f.criticidade === 'Muito Alta' ? 'white' :
                                                        f.criticidade === 'Alta' ? '#B91C1C' :
                                                            f.criticidade === 'Média' ? '#92400E' : '#0369A1',
                                                    fontWeight: 700
                                                }} />
                                            </TableCell>
                                            <TableCell>{new Date(f.data_auditoria).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )
            }

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Container >
    );
};

export default AuditoriaSus;
